import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../services/api';
import RemindersManager from '../components/RemindersManager';
import NotificationManager from '../components/NotificationManager';
import AdminNotificationSender from '../components/AdminNotificationSender';
import ProfilePhoto from '../components/ProfilePhoto';
import BiometricSetup from '../components/BiometricSetup';
import { useBiometricAuth } from '../hooks/useBiometricAuth';
import { usePWA } from '../hooks/usePWA';
// import { useAppUpdate } from '../context/AppUpdateContext'; // ELIMINADO
import './Profile.css';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const { isPWA } = usePWA();
  const { isAvailable, hasCredentials, devices } = useBiometricAuth();
  // const { checkForUpdate, hasUpdateAvailable, isCheckingUpdate } = useAppUpdate(); // ELIMINADO
  // Funciones simplificadas para reemplazar funcionalidad de updates
  const checkForUpdate = () => window.location.reload();
  const hasUpdateAvailable = false;
  const isCheckingUpdate = false;
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    follow_up_notifications: user?.follow_up_notifications ?? true,
    follow_up_enabled: user?.follow_up_enabled ?? true,
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await usersAPI.updateProfile(formData);
      setSuccess('Perfil actualizado correctamente');
      setIsEditing(false);
      
      // Recargar datos del usuario
      window.location.reload();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al actualizar perfil');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Las nuevas contraseñas no coinciden');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    // Validar formato de contraseña
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
    if (!passwordRegex.test(passwordData.newPassword)) {
      setError('La nueva contraseña debe contener al menos una minúscula, una mayúscula y un número');
      return;
    }

    setIsLoading(true);

    try {
      await usersAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      setSuccess('Contraseña cambiada correctamente');
      setIsChangingPassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cambiar contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const password = window.prompt('Para eliminar tu cuenta, ingresa tu contraseña:');
    if (!password) return;

    if (!window.confirm('¿Estás completamente seguro? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setIsLoading(true);
      await usersAPI.deleteAccount(password);
      
      // Redirigir al login
      window.location.href = '/login';
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar cuenta');
      setIsLoading(false);
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Mi Perfil</h1>
        {!isEditing && (
          <button
            className="btn-small btn-primary"
            onClick={() => setIsEditing(true)}
          >
            ✏️ Editar Perfil
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      <div className="profile-grid">
        {/* Foto de perfil */}
        <div className="profile-card">
          <h2>Foto de Perfil</h2>
          <ProfilePhoto />
        </div>

        {/* Información del perfil */}
        <div className="profile-card">
          <h2>Información Personal</h2>
          
          {isEditing ? (
            <form onSubmit={handleProfileSubmit}>
              <div className="form-group">
                <label htmlFor="name">Nombre completo</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={user?.email}
                  disabled
                  className="disabled-input"
                />
                <small className="form-help">
                  El email no se puede cambiar
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Teléfono</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Tu número de teléfono"
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="follow_up_notifications"
                    checked={formData.follow_up_notifications}
                    onChange={handleInputChange}
                  />
                  <span>Recibir notificaciones de seguimiento por email</span>
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="follow_up_enabled"
                    checked={formData.follow_up_enabled}
                    onChange={handleInputChange}
                  />
                  <span>Habilitar sistema de seguimiento automático</span>
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          ) : (
            <div className="profile-info">
              <div className="info-item">
                <label>Nombre:</label>
                <span>{user?.name}</span>
              </div>
              <div className="info-item">
                <label>Email:</label>
                <span>{user?.email}</span>
              </div>
              <div className="info-item">
                <label>Teléfono:</label>
                <span>{user?.phone || 'No especificado'}</span>
              </div>
              <div className="info-item">
                <label>Notificaciones:</label>
                <span>{user?.follow_up_notifications ? '✅ Activadas' : '❌ Desactivadas'}</span>
              </div>
              <div className="info-item">
                <label>Seguimiento:</label>
                <span>{user?.follow_up_enabled ? '✅ Habilitado' : '❌ Deshabilitado'}</span>
              </div>
              <div className="info-item">
                <label>Cuenta creada:</label>
                <span>{(() => {
                  if (!user?.created_at) return 'Sin fecha';
                  try {
                    const date = new Date(user.created_at);
                    if (isNaN(date.getTime())) return 'Fecha inválida';
                    return date.toLocaleDateString('es-ES', {
                      day: '2-digit',
                      month: '2-digit', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  } catch (error) {
                    return 'Error en fecha';
                  }
                })()}</span>
              </div>
            </div>
          )}
        </div>

        {/* Cambiar contraseña */}
        <div className="profile-card">
          <h2>Seguridad</h2>
          
          {user?.auth_provider === 'google' ? (
            <div className="google-security-info">
              <div className="google-auth-notice">
                <span className="google-icon">🔐</span>
                <div>
                  <h3>Cuenta de Google</h3>
                  <p>Tu cuenta está vinculada con Google. Para cambiar tu contraseña, debes hacerlo desde tu cuenta de Google.</p>
                </div>
              </div>
              <a 
                href="https://myaccount.google.com/security" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary google-security-btn"
              >
                🔒 Gestionar Seguridad en Google
              </a>
            </div>
          ) : !isChangingPassword ? (
            <div className="security-actions">
              <p>Mantén tu cuenta segura cambiando tu contraseña regularmente.</p>
              <button
                className="btn-primary"
                onClick={() => setIsChangingPassword(true)}
              >
                Cambiar Contraseña
              </button>
            </div>
          ) : (
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label htmlFor="currentPassword">Contraseña actual</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">Nueva contraseña</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                />
                <small className="form-help">
                  Mínimo 6 caracteres con mayúscula, minúscula y número
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmar nueva contraseña</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setIsChangingPassword(false)}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Cambiando...' : 'Cambiar Contraseña'}
                </button>
              </div>
            </form>
          )}
          
          {/* Autenticación Biométrica - Solo en PWA */}
          {isPWA && isAvailable && (
            <div className="biometric-section">
              <hr className="section-divider" />
              <h3>🔐 Autenticación Biométrica</h3>
              <p>
                {hasCredentials 
                  ? `Tienes ${devices.length} dispositivo${devices.length !== 1 ? 's' : ''} configurado${devices.length !== 1 ? 's' : ''} para acceso biométrico.`
                  : 'Configura tu huella digital o Face ID para acceder más rápido sin contraseña.'
                }
              </p>
              
              {hasCredentials && devices.length > 0 && (
                <div className="biometric-devices-preview">
                  {devices.slice(0, 2).map((device) => (
                    <div key={device.id} className="device-preview">
                      <span className="device-icon">🔒</span>
                      <div className="device-details">
                        <strong>{device.device_name}</strong>
                        <small>
                          {device.last_used_at 
                            ? `Último uso: ${new Date(device.last_used_at).toLocaleDateString()}`
                            : `Creado: ${new Date(device.created_at).toLocaleDateString()}`
                          }
                        </small>
                      </div>
                    </div>
                  ))}
                  {devices.length > 2 && (
                    <div className="more-devices">
                      +{devices.length - 2} más
                    </div>
                  )}
                </div>
              )}
              
              <button
                className="btn-primary biometric-setup-btn"
                onClick={() => setShowBiometricSetup(true)}
              >
                {hasCredentials ? '⚙️ Gestionar Dispositivos' : '🔐 Configurar Biometría'}
              </button>
            </div>
          )}

          {/* Actualizaciones de la App - PWA o Admin */}
          {(isPWA || user?.is_admin || user?.admin_role) && (
            <div className="app-update-section">
              <hr className="section-divider" />
              <h3>🔄 Actualizaciones de la App</h3>
              <p>
                {!isPWA 
                  ? 'Esta función está optimizada para PWA. En navegador web, la comprobación es limitada.'
                  : hasUpdateAvailable 
                    ? 'Hay una nueva versión disponible. Actualiza para obtener las últimas funciones y mejoras.'
                    : 'Tu app está actualizada. Puedes forzar una comprobación de actualizaciones.'
                }
              </p>
              
              <button
                className={`btn-primary ${hasUpdateAvailable ? 'update-available' : ''}`}
                onClick={checkForUpdate}
                disabled={isCheckingUpdate}
              >
                {isCheckingUpdate ? (
                  <>
                    <div className="spinner" style={{ width: 16, height: 16, margin: 0 }}></div>
                    Comprobando...
                  </>
                ) : hasUpdateAvailable ? (
                  '⬆️ Actualizar Ahora'
                ) : (
                  '🔍 Buscar Actualizaciones'
                )}
              </button>
            </div>
          )}
        </div>

        {/* Notificaciones y Recordatorios */}
        <div className="profile-card">
          <h2>🔔 Notificaciones y Recordatorios</h2>
          <p>Gestiona tus recordatorios personalizados y configuración de notificaciones.</p>
          
          <div className="notifications-section">
            <div className="notification-item">
              <div className="notification-info">
                <h3>📅 Recordatorios Personales</h3>
                <p>Configura recordatorios para medir tu tensión, registrar dolores o tomar medicación.</p>
              </div>
              <div className="notification-action">
                <RemindersManager className="profile-reminder-manager" />
              </div>
            </div>

            {user?.is_admin && (
              <>
                <div className="notification-item">
                  <div className="notification-info">
                    <h3>📢 Envío Masivo</h3>
                    <p>Envía notificaciones a todos los usuarios de la aplicación.</p>
                  </div>
                  <div className="notification-action">
                    <AdminNotificationSender className="profile-admin-sender" />
                  </div>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <h3>🔔 Gestión de Notificaciones</h3>
                    <p>Prueba y gestiona el sistema de notificaciones de la aplicación.</p>
                  </div>
                  <div className="notification-action">
                    <NotificationManager className="profile-notification-manager" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Zona de peligro */}
        <div className="profile-card danger-zone">
          <h2>Zona de Peligro</h2>
          <p>
            Una vez que elimines tu cuenta, no hay vuelta atrás. 
            Todos tus datos serán eliminados permanentemente.
          </p>
          <button
            className="btn-danger"
            onClick={handleDeleteAccount}
            disabled={isLoading}
          >
            Eliminar Cuenta
          </button>
        </div>
      </div>
      
      {/* Modal de configuración biométrica */}
      {showBiometricSetup && (
        <BiometricSetup
          isOpen={showBiometricSetup}
          onClose={() => setShowBiometricSetup(false)}
          onSuccess={() => {
            // Opcional: mostrar mensaje de éxito
            console.log('Biometric setup completed');
          }}
        />
      )}
    </div>
  );
};

export default Profile;